/**
 * Data block: reads a transform property (translation, rotation, scale)
 * from a SceneObject's Babylon transform node.
 *
 * Implements the KHR_interactivity `pointer/get` operation using our internal
 * TransformPath addressing. Pure data node — no flow sockets. Output is
 * pulled when a downstream node needs the value.
 *
 * Output format:
 *   - "translation" → Vector3 from node.position
 *   - "rotation"    → Quaternion from node.rotationQuaternion
 *   - "scale"       → Vector3 from node.scaling
 *
 * Outputs are Babylon math instances (not raw arrays) so the value survives
 * polymorphic math blocks, which dispatch on getClassName(). Raw arrays fall
 * through to the scalar branch and produce NaN. Cloned so downstream math
 * can't mutate the live transform.
 */

import { FlowGraphBlock } from "@babylonjs/core/FlowGraph/flowGraphBlock";
import type { FlowGraphContext } from "@babylonjs/core/FlowGraph/flowGraphContext";
import type { IFlowGraphBlockConfiguration } from "@babylonjs/core/FlowGraph/flowGraphBlock";
import { RichTypeAny } from "@babylonjs/core/FlowGraph/flowGraphRichTypes";
import { Quaternion } from '@babylonjs/core/Maths/math.vector';
import { resolveTarget } from './traceContext';
import { Logger } from '../../../utils/Logger';
export class PointerGetBlock extends FlowGraphBlock {
    private _targetPath: string;
    private _property: string;

    constructor(config?: IFlowGraphBlockConfiguration & { target?: string; property?: string }) {
        super(config);
        this._targetPath = config?.target ?? '';
        this._property = config?.property ?? 'translation';
        this.registerDataOutput("value", RichTypeAny);
    }

    public override _updateOutputs(context: FlowGraphContext): void {
        const target = resolveTarget(context, this._targetPath);
        if (!target) {
            this.getDataOutput("value")!.setValue(undefined, context);
            return;
        }

        const transform = target.transform;
        let value: unknown;

        switch (this._property) {
            case 'translation': {
                value = transform.position.clone();
                break;
            }
            case 'rotation': {
                const q = transform.rotationQuaternion;
                if (q) {
                    value = q.clone();
                } else {
                    // Fall back to euler rotation → proper quaternion
                    const r = transform.rotation;
                    value = Quaternion.FromEulerAngles(r.x, r.y, r.z);
                }
                break;
            }
            case 'scale': {
                value = transform.scaling.clone();
                break;
            }
            default:
                Logger.warn(`[PointerGetBlock] Unknown property "${this._property}" on "${target.name}"`);
        }

        this.getDataOutput("value")!.setValue(value, context);
    }

    public override getClassName(): string {
        return "PointerGetBlock";
    }
}
